import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle, X } from 'lucide-react';
import PageLoader from '../components/PageLoader';
import LoadMoreButton from '../components/LoadMoreButton';
import { authConfig } from '../config/auth';
import { useAuth } from '../contexts/AuthContext';
import { usePaginatedFetch } from '../hooks/usePaginatedFetch';
import type { ClaimedReward } from '@crewcast/shared';
import '../styles/rewards.css';

type StatusFilter = 'claimed' | 'fulfilled' | 'cancelled' | 'all';

type UserSummary = {
  userId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
};

export default function AdminClaimedRewardsPage() {
  const { token } = useAuth();
  const [filter, setFilter] = useState<StatusFilter>('claimed');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [usersById, setUsersById] = useState<Record<string, UserSummary>>({});
  const [mutationError, setMutationError] = useState('');
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<ClaimedReward | null>(null);
  const [cancelFormError, setCancelFormError] = useState('');
  const [cancelSuccessMessage, setCancelSuccessMessage] = useState('');
  const navigate = useNavigate();

  const fetchParams = useMemo(
    () => (filter !== 'all' ? { status: filter } : undefined),
    [filter]
  );

  const {
    items: claims,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
    refresh: refreshClaims,
  } = usePaginatedFetch<ClaimedReward>({
    url: `${authConfig.apiUrl}/api/admin/claimed-rewards`,
    token,
    dataKey: 'claimedRewards',
    limit: 25,
    params: fetchParams,
  });

  const refreshSidebarCounts = () => {
    window.dispatchEvent(new Event('crewcast:refresh-admin-counts'));
  };

  useEffect(() => {
    // After claims load/update, fetch user details for any missing users (cached).
    const uniqueUserIds = Array.from(new Set(claims.map((c) => c.userId))).filter(Boolean);
    const missing = uniqueUserIds.filter((id) => !usersById[id]);
    if (missing.length === 0) return;

    void fetchUsers(missing);
    // We intentionally depend on claims + usersById so we only fetch what's missing.
  }, [claims, usersById]);

  const fetchUsers = async (userIds: string[]) => {
    try {
      if (!token) return;

      // Fetch in parallel; cache results.
      const results = await Promise.all(
        userIds.map(async (userId) => {
          try {
            const resp = await fetch(`${authConfig.apiUrl}/api/admin/employees/${userId}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const data = await resp.json().catch(() => ({}));
            if (!resp.ok) return null;
            const emp = data.employee;
            if (!emp) return null;
            return {
              userId,
              firstName: emp.firstName,
              lastName: emp.lastName,
              email: emp.email,
            } as UserSummary;
          } catch {
            return null;
          }
        })
      );

      const next: Record<string, UserSummary> = {};
      for (const u of results) {
        if (u?.userId) next[u.userId] = u;
      }
      if (Object.keys(next).length > 0) {
        setUsersById((prev) => ({ ...prev, ...next }));
      }
    } catch {
      // Non-fatal: leave userId fallback.
    }
  };

  const renderUserLabel = (userId: string) => {
    const u = usersById[userId];
    if (!u) return userId;
    const name = `${u.firstName || ''} ${u.lastName || ''}`.trim();
    return u.email ? `${name || userId} (${u.email})` : name || userId;
  };


  const fulfillClaim = async (claimId: string) => {
    setProcessingId(claimId);
    setMutationError('');
    try {
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(
        `${authConfig.apiUrl}/api/admin/claimed-rewards/${claimId}/fulfill`,
        {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fulfill claim');
      }

      refreshClaims();
    } catch (err: any) {
      setMutationError(err.message || 'Failed to fulfill claim');
    } finally {
      setProcessingId(null);
    }
  };

  const openCancelModal = (claim: ClaimedReward) => {
    setCancelTarget(claim);
    setCancelFormError('');
    setCancelSuccessMessage('');
    setCancelModalOpen(true);
  };

  const closeCancelModal = () => {
    setCancelModalOpen(false);
    setCancelTarget(null);
    setCancelFormError('');
    setCancelSuccessMessage('');
  };

  const confirmCancelClaim = async () => {
    if (!cancelTarget) return;

    setProcessingId(cancelTarget.claimId);
    setCancelFormError('');
    setMutationError('');

    try {
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(
        `${authConfig.apiUrl}/api/admin/claimed-rewards/${cancelTarget.claimId}/cancel`,
        {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Failed to cancel claim');
      }

      // Refresh list + sidebar badge
      refreshClaims();

      setCancelSuccessMessage('Claim cancelled. Points refunded to the employee.');
      setTimeout(() => closeCancelModal(), 1200);
    } catch (err: any) {
      setCancelFormError(err.message || 'Failed to cancel claim');
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <div className="rewards-container">
      <div className="rewards-header">
        <div>
          <h1 className="rewards-title">Claimed Rewards</h1>
          <p className="rewards-subtitle">Review reward claims</p>
        </div>

        <button className="rewards-secondary-btn" onClick={() => navigate('/rewards')}>
          Back to Rewards
        </button>
      </div>

      {(error || mutationError) && <div className="rewards-error">Error: {error || mutationError}</div>}

      <div className="rewards-filter">
        <label htmlFor="status-filter" className="rewards-filter-label">Status</label>
        <select
          id="status-filter"
          className="rewards-select"
          value={filter}
          onChange={(e) => setFilter(e.target.value as StatusFilter)}
        >
          <option value="claimed">Claimed</option>
          <option value="fulfilled">Fulfilled</option>
          <option value="cancelled">Cancelled</option>
          <option value="all">All</option>
        </select>
      </div>

      {loading ? (
        <PageLoader />
      ) : claims.length === 0 ? (
        <div className="rewards-empty">
          <div className="rewards-empty-icon">
              <CheckCircle size={48} color="#00bf62" />
            </div>
          <h2>No claims</h2>
          <p>Nothing to review for this filter.</p>
        </div>
      ) : (
        <div className="rewards-grid">
          {claims.map((c) => {
            const isClaimed = c.status === 'claimed';
            return (
              <div key={c.claimId} className="rewards-card">
                {c.image && (
                  <div className="rewards-card-image">
                    <img src={c.image} alt={c.title} />
                  </div>
                )}
                <div className="rewards-card-content">
                  <h3 className="rewards-card-title">{c.title}</h3>
                  <div style={{ fontSize: 12, color: 'var(--color-gray-600)', marginBottom: 8 }}>
                    User: {renderUserLabel(c.userId)}
                  </div>
                  <div className="rewards-card-footer">
                    <span className="rewards-card-points">{c.pointsToClaim} pts</span>
                    <span
                      className={`rewards-status-badge ${
                        c.status === 'claimed'
                          ? 'pending'
                          : c.status === 'fulfilled'
                          ? 'approved'
                          : 'rejected'
                      }`}
                    >
                      {c.status}
                    </span>
                  </div>

                  <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                    <button
                      className="rewards-primary-btn"
                      disabled={!isClaimed || processingId === c.claimId}
                      onClick={() => fulfillClaim(c.claimId)}
                      style={{ flex: 1 }}
                    >
                      {processingId === c.claimId ? 'Working...' : 'Fulfill'}
                    </button>
                    <button
                      className="rewards-danger-btn"
                      disabled={!isClaimed || processingId === c.claimId}
                      onClick={() => openCancelModal(c)}
                      style={{ flex: 1 }}
                    >
                      {processingId === c.claimId ? 'Working...' : 'Cancel'}
                    </button>
                  </div>

                  <div style={{ marginTop: 8, fontSize: 12, color: 'var(--color-gray-500)' }}>
                    Claimed {new Date(c.claimedAt).toLocaleString()}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <LoadMoreButton onClick={loadMore} loading={loadingMore} hasMore={hasMore} />

      {cancelModalOpen && cancelTarget && (
        <div
          className="crewcast-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cancel-claim-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && processingId === null) closeCancelModal();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && processingId === null) closeCancelModal();
          }}
          tabIndex={-1}
        >
          <div className="crewcast-modal-card">
            <div className="crewcast-modal-header">
              <div>
                <h2 id="cancel-claim-title" className="crewcast-modal-title">
                  Cancel reward claim?
                </h2>
                <div className="crewcast-modal-subtitle">
                  {cancelTarget.title} • {cancelTarget.pointsToClaim} pts
                </div>
              </div>
              <button
                type="button"
                className="crewcast-modal-close"
                onClick={() => (processingId === null ? closeCancelModal() : null)}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="crewcast-modal-body">
              <div className="crewcast-modal-hint">
                <AlertCircle size={16} />
                <span>
                  This will cancel the claim and refund the points to the employee. This action cannot be undone.
                </span>
              </div>

              {cancelFormError && (
                <div className="crewcast-modal-error">{cancelFormError}</div>
              )}

              {cancelSuccessMessage && (
                <div className="crewcast-modal-success">✓ {cancelSuccessMessage}</div>
              )}
            </div>

            <div className="crewcast-modal-actions">
              <button
                type="button"
                className="crewcast-modal-btn secondary"
                onClick={() => (processingId === null ? closeCancelModal() : null)}
                disabled={processingId !== null}
              >
                Keep claim
              </button>
              <button
                type="button"
                className="crewcast-modal-btn danger"
                onClick={confirmCancelClaim}
                disabled={processingId !== null}
              >
                {processingId ? 'Cancelling…' : 'Cancel claim'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


